import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { FeedCategory, FeedIcon as FeedIconKind } from "../types";

/**
 * Who an entry came from, as a colour and a label. These are source
 * categories, not status: none of them says a reading is good or bad, so
 * they do not use the theme's success/warning/error roles.
 */
export const CATEGORY: Record<FeedCategory, { label: string; color: string; tint: string }> = {
  system: { label: "System Event", color: "#3E6DAF", tint: "#E7F0FA" },
  device: { label: "Device Event", color: "#2F8F5B", tint: "#E6F3EC" },
  clinician: { label: "Clinician Entry", color: "#7B5CC4", tint: "#EFEAF9" },
  ai: { label: "AI-generated Summary", color: "#B7791F", tint: "#FBF1DD" },
};

/** Icon colours that differ from their category's (the screenshot's orange truck). */
const ICON_COLOR: Partial<Record<FeedIconKind, string>> = { transport: "#E07B39" };

const Round = styled(Box, { shouldForwardProp: (p) => p !== "fill" && p !== "square" })<{
  fill: string;
  square?: boolean;
}>(({ fill, square }) => ({
  width: square ? 32 : 26,
  height: square ? 32 : 26,
  flex: "none",
  borderRadius: square ? 8 : "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: fill,
  color: "#fff",
  "& svg": { fontSize: square ? 18 : 15 },
}));

const Letter = styled("span")(() => ({ fontWeight: 800, fontSize: "0.8rem", lineHeight: 1 }));

function Glyph(props: { icon: FeedIconKind }) {
  switch (props.icon) {
    case "system":
      return <SettingsOutlinedIcon />;
    case "transport":
      return <LocalShippingOutlinedIcon />;
    case "destination":
      return <Letter>H</Letter>;
    case "device":
      return <MonitorHeartOutlinedIcon />;
    case "clinician":
      return <AssignmentOutlinedIcon />;
    default:
      return <AutoAwesomeOutlinedIcon />;
  }
}

type FeedIconProps = {
  category: FeedCategory;
  icon: FeedIconKind;
  /** Solid circle (timeline) or a tinted rounded square (updates list). */
  variant?: "solid" | "soft";
};

export function FeedIcon(props: FeedIconProps) {
  const meta = CATEGORY[props.category];
  const color = ICON_COLOR[props.icon] ?? (props.icon === "destination" ? CATEGORY.system.color : meta.color);
  if (props.variant === "soft") {
    return (
      <Round fill={meta.tint} square sx={{ color }}>
        <Glyph icon={props.icon} />
      </Round>
    );
  }
  return (
    <Round fill={color}>
      <Glyph icon={props.icon} />
    </Round>
  );
}

/** The dashed circle of a step that has not happened yet. */
export const PendingIcon = styled(Box)(({ theme }) => ({
  width: 26,
  height: 26,
  flex: "none",
  borderRadius: "50%",
  border: `2px dashed ${theme.palette.text.disabled}`,
  background: theme.palette.background.paper,
}));

export function clock(at: string): string {
  const parsed = new Date(at);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
